/**
 * Lo derivado de una carga de salida: su estado, su reserva, sus avisos y los
 * totales de la pantalla.
 *
 * Dos criterios que se heredan y no se reinventan:
 *
 * - **El porcentaje de picking se mide sobre lo SOLICITADO**, igual que
 *   `ResumenPicking.pct` en `cargasPicking.ts`. El mockup del cliente lo medía
 *   sobre lo reservado; se descartó porque la misma carga enseñaría 48 % aquí y
 *   72 % en Pedidos de venta, y ése es el descuadre que luego nadie puede
 *   resolver. La reserva se enseña aparte, con su propio hueco.
 * - **El hueco de peso no es cero.** `pesoNeto`/`pesoBruto`/`cajas` a `null`
 *   significan «el maestro de artículos no lo sabe», así que los totales cuentan
 *   los huecos y los declaran en vez de sumarlos como ceros.
 */
import type { ArticuloCatalogo, CargaEstadoPicking, CargaSalida, CargaSalidaLinea } from '@/types'
import type { OpcionMulti } from '@/components/common/MultiFiltro'
import { catalogoArticulos, type FmtArticulo } from '@/utils/filtroArticulo'
import { diasRetraso } from '@/utils/agruparPorFecha'

/** Fecha ISO `yyyy-mm-dd` → `Date` local, o `null`. */
export function fechaDe(iso?: string | null): Date | null {
  if (!iso) return null
  const [y, m, d] = iso.split('-').map(Number)
  if (!y || !m || !d) return null
  return new Date(y, m - 1, d)
}

/** Una línea de la celda de palets de una carga. */
export interface LineaPalets { clave: 'real' | 'mixto' | 'mono'; valor: number }

/**
 * Qué líneas enseña la celda de palets.
 *
 * Definitivo → una sola (los estimados no pintan nada). Estimado → mixto y
 * mono. Sin dato (`paletsSonReales` null/ausente) → lista vacía: la celda pinta
 * «sin dato» y guion, nunca un 0. Una cifra que falta no se inventa como cero.
 */
export function lineasPalets(c: CargaSalida): LineaPalets[] {
  if (c.paletsSonReales == null) return []
  if (c.paletsSonReales) {
    return c.paletsReales == null ? [] : [{ clave: 'real', valor: c.paletsReales }]
  }
  const l: LineaPalets[] = []
  if (c.paletsEstimadosMixto != null) l.push({ clave: 'mixto', valor: c.paletsEstimadosMixto })
  if (c.paletsEstimadosMono != null) l.push({ clave: 'mono', valor: c.paletsEstimadosMono })
  return l
}

/** Una línea de la celda de peso de expedición (bruto + palet). */
export interface LineaPesoExpedicion { clave: 'real' | 'mixto' | 'mono' | 'sinPalet' | 'sinDato'; valor: number }

/**
 * Qué líneas enseña la celda «bruto + palet»: lo que pesa el ENVÍO, no la
 * mercancía (eso es «Neto / bruto»).
 *
 * Mismo reparto que `lineasPalets`: real → una línea; estimado → mixto y mono.
 * Sin palet hay DOS casos que enseñan el mismo bruto y significan lo contrario:
 *  - `sinPalet` (`cargaSinPalet`, 7 cargas vivas): la carga viaja sin paletizar.
 *    Es un hecho de negocio y el bruto ES el peso de expedición: dato COMPLETO.
 *  - `sinDato` (`paletsSonReales` NULL, 116 cargas vivas): no sabemos si lleva
 *    palet ni cuánto pesa. El bruto es lo único que hay: cifra PARCIAL.
 * Con chips iguales se diría «no lleva palet» y «no lo sabemos» a la vez. Si
 * `cargaSinPalet` no llega (undefined) se trata como `sinDato`: lo prudente.
 * Sin bruto → lista vacía (guion): el hueco no es cero.
 */
export function lineasPesoExpedicion(c: CargaSalida): LineaPesoExpedicion[] {
  if (c.pesoBruto == null) return []
  const bruto = c.pesoBruto
  if (c.cargaSinPalet === true) return [{ clave: 'sinPalet', valor: bruto }]
  const l: LineaPesoExpedicion[] = []
  if (c.paletsSonReales === true) {
    if (c.pesoPalets != null) l.push({ clave: 'real', valor: bruto + c.pesoPalets })
  } else if (c.paletsSonReales === false) {
    if (c.pesoPaletsMixto != null) l.push({ clave: 'mixto', valor: bruto + c.pesoPaletsMixto })
    if (c.pesoPaletsMono != null) l.push({ clave: 'mono', valor: bruto + c.pesoPaletsMono })
  }
  return l.length > 0 ? l : [{ clave: 'sinDato', valor: bruto }]
}

/**
 * El peso de EXPEDICIÓN en una sola cifra, para sumar.
 *
 * Misma regla que la cifra única de palets (`_palets_mandante` en el backend):
 * la real si la hay y el **MONO** si es estimada, que es la conservadora. Por
 * exceso sobra sitio en el camión; por defecto se queda carga en tierra.
 *
 * Se apoya en `lineasPesoExpedicion` y no repite el reparto de casos: son la
 * misma pregunta contestada para pintar y para sumar, y escribirla dos veces
 * es cómo la celda y el total acaban discrepando. `null` cuando no hay bruto o
 * cuando no se sabe si lleva palet — el hueco NO es cero.
 */
export function pesoExpedicionMandante(c: CargaSalida): number | null {
  const lineas = lineasPesoExpedicion(c)
  if (lineas.length === 0) return null
  const mono = lineas.find(l => l.clave === 'mono')
  if (mono) return mono.valor
  const real = lineas.find(l => l.clave === 'real' || l.clave === 'sinPalet')
  // `sinDato` es el bruto a secas: es una cifra PARCIAL y no se suma como si
  // fuera el peso de expedición, o el total diría que pesa menos de lo que pesa.
  return real ? real.valor : null
}

/** Los tres modos que la pantalla sabe pintar con icono. Cualquier otro valor
 *  que llegue del origen se enseña tal cual, sin chip: inventarle un icono a un
 *  valor que no conocemos es peor que no ponerle ninguno. */
export type ModoConocido = 'AEREO' | 'MARITIMO' | 'TERRESTRE'

export const MODOS_CONOCIDOS: readonly ModoConocido[] = ['AEREO', 'MARITIMO', 'TERRESTRE']

export function esModoConocido(v: string): v is ModoConocido {
  return (MODOS_CONOCIDOS as readonly string[]).includes(v)
}

/**
 * El modo de transporte de una carga, con la contradicción del origen RESUELTA.
 *
 * **Si la carga está marcada como aérea, es AÉREO**; si no, vale lo que diga
 * `modoTransporte`. Son dos campos que rellenan procesos distintos y no se
 * hablan: `aerea` sale de la plantilla de carga —y es la que decide si el
 * packing es aéreo, con sus tres niveles y sus air-boxes—, mientras que
 * `modoTransporte` es una clasificación de la tabla materializada.
 *
 * Que mande `aerea` no es arbitrario. De las 227 cargas marcadas como aéreas
 * (medido en octubre del 26), el modo sólo coincide en 32: **151 vienen vacías**
 * —ahí la regla rellena un hueco— y 44 dicen otra cosa (36 TERRESTRE, 8
 * MARITIMO) —ahí corrige—. Al revés no compensaría: haría que una carga con
 * packing aéreo se leyera como terrestre en la única columna que habla de
 * transporte.
 *
 * `null` cuando no hay ni lo uno ni lo otro: el 29 % de las cargas vivas. La
 * celda se queda en blanco y el filtro lo recoge en «sin informar»; no hay
 * TERRESTRE por defecto.
 */
export function modoTransporteDe(c: CargaSalida): string | null {
  if (c.aerea) return 'AEREO'
  return (c.modoTransporte ?? '').trim().toUpperCase() || null
}

/**
 * Clases del chip de cada modo. UN solo mapa, como `NIVEL_CHIP`.
 *
 * Relleno SUAVE y no sólido, al revés que el semáforo de preparación: aquí no
 * hay nada que alarme —un envío terrestre es lo normal— y el chip está para
 * leerse, no para saltar a la vista. El sólido se reserva para lo que avisa.
 *
 * **TERRESTRE va en gris a propósito**: son 353 de las 567 cargas vivas, el
 * 62 %. Pintar de color lo que le pasa a casi todo el mundo es la forma más
 * rápida de que el color deje de significar nada; el violeta del aéreo —3
 * cargas— y el azul del marítimo —48— se leen porque el resto no compite.
 * El violeta es el mismo que ya tenía la chapa de aérea, que es de donde viene.
 */
export const MODO_CHIP: Record<ModoConocido, { claro: string; oscuro: string }> = {
  AEREO:     { claro: 'bg-violet-100 text-violet-700', oscuro: 'bg-violet-500/15 text-violet-300' },
  MARITIMO:  { claro: 'bg-sky-100 text-sky-700',       oscuro: 'bg-sky-500/15 text-sky-300' },
  TERRESTRE: { claro: 'bg-slate-100 text-slate-600',   oscuro: 'bg-slate-500/15 text-slate-300' },
}

/** Estado de preparación, derivado de las cantidades: no hay columna de estado. */
export function estadoPickingDe(c: CargaSalida): CargaEstadoPicking {
  if (c.qtySolicitada <= 0) return 'pendiente'
  if (c.qtyPicking <= 0) return 'pendiente'
  return c.qtyPicking >= c.qtySolicitada ? 'preparada' : 'preparando'
}

/** Avance del picking sobre lo SOLICITADO, 0–100. */
export function pickingPct(c: CargaSalida): number {
  if (c.qtySolicitada <= 0) return 0
  return Math.min(100, Math.max(0, (c.qtyPicking / c.qtySolicitada) * 100))
}

/** En qué punto está la reserva de una carga.
 *
 *  `completa` sigue siendo un booleano porque lo miran media docena de sitios
 *  —el nivel de la fila, la alarma, la incidencia, la celda y el Excel— y
 *  cambiarles la pregunta no aportaba nada. `estado` es lo que faltaba: hasta
 *  ahora «no completa» mezclaba la carga que no tiene NADA reservado con la que
 *  tiene casi todo, y son dos situaciones distintas para quien prepara. */
export type EstadoReserva = 'sin' | 'parcial' | 'completa'

export interface ResumenReserva {
  pct: number
  /** Unidades que faltan por atar a stock. `0` cuando está completa. */
  falta: number
  completa: boolean
  estado: EstadoReserva
}

export function reservaDe(c: CargaSalida): ResumenReserva {
  // Una carga sin unidades solicitadas no tiene nada que reservar: se cuenta
  // como completa, que es lo que hacía antes y lo que miran los consumidores.
  if (c.qtySolicitada <= 0) return { pct: 0, falta: 0, completa: true, estado: 'completa' }
  const pct = Math.min(100, Math.max(0, (c.qtyReservada / c.qtySolicitada) * 100))
  const falta = Math.max(0, c.qtySolicitada - c.qtyReservada)
  const completa = falta === 0
  // El cero es exacto a propósito: «sin reserva» significa que no se ha atado
  // ni una unidad, no «casi nada». Media unidad ya es haber empezado.
  const estado: EstadoReserva = completa ? 'completa' : c.qtyReservada <= 0 ? 'sin' : 'parcial'
  return { pct, falta, completa, estado }
}

/**
 * ¿Va a llegar tarde?
 *
 * Es DERIVADO y no un estado guardado: sale hoy o antes y el picking no está
 * terminado. No hay ningún campo en origen que diga esto, y guardarlo lo dejaría
 * congelado cuando cambiara el picking.
 */
export function enRiesgo(c: CargaSalida, hoy = new Date()): boolean {
  if (c.expedida) return false
  const f = fechaDe(c.fechaEnvioPrevista)
  if (!f) return false
  const h = new Date(hoy)
  h.setHours(0, 0, 0, 0)
  f.setHours(0, 0, 0, 0)
  return f <= h && pickingPct(c) < 100
}

export type NivelCarga = 'expedida' | 'listo' | 'empezado' | 'reservado' | 'sinEmpezar'

/**
 * El semáforo de preparación, en orden de evaluación.
 *
 * Contesta «¿puedo expedir esto?», no «¿llega a tiempo?» — eso es `enRiesgo`,
 * que mira la FECHA y vive aparte, en la chapa roja junto al nº de carga.
 */
export function nivelDe(c: CargaSalida): NivelCarga {
  if (c.expedida) return 'expedida'
  if (pickingPct(c) >= 100) return 'listo'
  if (c.qtyPicking > 0) return 'empezado'
  if (reservaDe(c).completa) return 'reservado'
  return 'sinEmpezar'
}

/**
 * Los cinco niveles en orden de AVANCE, que es el que quiere el filtro.
 *
 * No es el de `nivelDe` —ése es de evaluación y empieza por `expedida`, porque
 * manda sobre todo lo demás— ni el alfabético, que colaría «Listo» entre
 * «Empezado» y «Reservado». Quien filtra por estado piensa en una escalera: lo
 * que no ha empezado arriba y lo que ya salió del almacén abajo.
 */
export const NIVELES_ORDEN: readonly NivelCarga[] = [
  'sinEmpezar', 'reservado', 'empezado', 'listo', 'expedida',
]

/**
 * Clases del chip de cada nivel. UN solo mapa, para que la pantalla y cualquier
 * leyenda futura no se separen (`cargasSalidaNivel.test.ts` lo amarra).
 *
 * **No hay rojo, a propósito.** `sinEmpezar` es gris: es el estado de 426 de las
 * 648 cargas vivas y 230 de ellas salen a más de una semana. El rojo se queda en
 * la chapa «EN RIESGO», que mira la fecha.
 *
 * **El relleno es SÓLIDO y el color vive en el FONDO, no en el texto.** Nació con
 * el color al 15 % de opacidad y el usuario no veía los chips: sobre blanco, un
 * `bg-emerald-500/15` acaba en un tono que hay que ir a buscar. Es el mismo error
 * que el semáforo de material cometió y corrigió en septiembre —ver el comentario
 * de `COMP_BADGE` en `production-sequence/sequenceFormat.ts`— y la razón es la
 * misma: un semáforo tiene que saltar a la vista recorriendo la tabla de reojo,
 * sin leerla. Si hay que buscarlo, no es un semáforo.
 *
 * Mismo tono en claro y en oscuro: el relleno sólido ya separa del fondo en los
 * dos, y dos valores distintos sólo darían ocasión de que se separasen.
 */
export const NIVEL_CHIP: Record<NivelCarga, { dark: string; light: string }> = {
  listo:      { dark: 'bg-emerald-600 text-white', light: 'bg-emerald-600 text-white' },
  empezado:   { dark: 'bg-amber-600 text-white',   light: 'bg-amber-600 text-white' },
  reservado:  { dark: 'bg-orange-600 text-white',  light: 'bg-orange-600 text-white' },
  sinEmpezar: { dark: 'bg-slate-500 text-white',   light: 'bg-slate-500 text-white' },
  expedida:   { dark: 'bg-cyan-600 text-white',    light: 'bg-cyan-600 text-white' },
}

/**
 * El tinte de FILA por nivel: franja lateral + fondo suave, el mismo idioma que
 * `COMP_ROW` (`production-sequence/sequenceFormat.ts`), en el mismo naranja que
 * el chip de `reservado`.
 *
 * **SÓLO `reservado` tiñe.** Es la fila accionable: hay stock atado y nadie ha
 * empezado a prepararla (medido 2026-10-08: 77 de 642 cargas vivas, el 12 %).
 * Los otros cuatro niveles NO tiñen, por la misma razón que «OK no tiñe» en el
 * semáforo de material: si se pintan muchas, la que hay que mirar deja de
 * destacar. En septiembre se probó a teñir la fila de producción del Gantt y se
 * revirtió el mismo día por saturación. No «completes» el mapa.
 *
 * Convivencia con lo demás que pinta la fila (`FilaCargaSalida`):
 * - **Marca del usuario (fucsia): MANDA el fondo.** Es una anotación personal y
 *   tiene que verse siempre; con el tinte debajo el fucsia no se distinguiría.
 *   La FRANJA se conserva igual, porque es otro canal (borde, no fondo) y así la
 *   fila marcada sigue diciendo que está reservada.
 * - **Chapa roja «EN RIESGO»:** vive dentro de la celda, no compite por el
 *   fondo; es una señal distinta (mira la fecha) y se lee sobre el naranja.
 * - El resaltado de búsqueda no existe en esta tabla: el texto sólo filtra.
 *
 * El borde va en `border-l-<color>` y no en `border-<color>`: la fila ya lleva
 * su `border-t` de separación y un color de borde a secas se lo teñiría.
 */
export const NIVEL_FILA: Record<NivelCarga, { claro: string; oscuro: string; franjaClaro: string; franjaOscuro: string }> = {
  listo:      { claro: '', oscuro: '', franjaClaro: '', franjaOscuro: '' },
  empezado:   { claro: '', oscuro: '', franjaClaro: '', franjaOscuro: '' },
  reservado: {
    claro:  'bg-orange-200 hover:bg-orange-300',
    oscuro: 'bg-orange-900/35 hover:bg-orange-900/50',
    franjaClaro:  'border-l-4 border-l-orange-600',
    franjaOscuro: 'border-l-4 border-l-orange-500',
  },
  sinEmpezar: { claro: '', oscuro: '', franjaClaro: '', franjaOscuro: '' },
  expedida:   { claro: '', oscuro: '', franjaClaro: '', franjaOscuro: '' },
}

/**
 * Clases de la fila de una carga: fondo + franja. `fondoMarca` es lo que pinta
 * la fila cuando el usuario la ha marcado (manda sobre el tinte del nivel).
 * Devuelve `fondo` vacío si no hay nada que decir para que el llamante use su
 * hover de siempre.
 */
export function clasesFilaNivel(
  nivel: NivelCarga, dark: boolean, marcada: boolean,
): { fondo: string; franja: string } {
  const f = NIVEL_FILA[nivel]
  return {
    fondo: marcada ? '' : (dark ? f.oscuro : f.claro),
    franja: dark ? f.franjaOscuro : f.franjaClaro,
  }
}

/**
 * ¿Está la fila en ALARMA? Reserva al 100 %, picking incompleto y salida hoy o
 * antes: «nada lo impide y va tarde» — todo el stock está atado, no hay excusa.
 *
 * Medido 2026-10-08 sobre 615 cargas vivas: 62 (10 %), minoría clara. 18 de
 * ellas son `reservado` (naranja) hoy: por eso `clasesFilaCarga` evalúa el rojo
 * ANTES que el naranja. Es un subconjunto de `enRiesgo` (138 cargas). No
 * simplificar la regla.
 */
export function filaEnAlarma(c: CargaSalida, hoy = new Date()): boolean {
  return enRiesgo(c, hoy) && reservaDe(c).completa
}

/** Tinte rojo de la fila en alarma: mismo idioma que `NIVEL_FILA` (franja + fondo). */
export const FILA_ALARMA = {
  claro:  'bg-red-200 hover:bg-red-300',
  oscuro: 'bg-red-900/35 hover:bg-red-900/50',
  franjaClaro:  'border-l-4 border-l-red-600',
  franjaOscuro: 'border-l-4 border-l-red-500',
}

/**
 * Clases de la chapa «EN RIESGO». Sobre fila roja (`sobreAlarma`) va en relleno
 * SÓLIDO: un tinte rojo sobre un fondo rojo no se lee; el color vive en el fondo.
 */
export const CHAPA_RIESGO = {
  claro:  'bg-red-100 text-red-700',
  oscuro: 'bg-red-500/15 text-red-300',
  sobreAlarma: 'bg-red-600 text-white',
}

/**
 * Tinte de la fila de una carga. PRECEDENCIA: la alarma (rojo) se evalúa
 * PRIMERO y gana al naranja de `reservado`. La marca del usuario manda sobre el
 * FONDO (igual que en `clasesFilaNivel`) y la franja se conserva.
 */
export function clasesFilaCarga(
  c: CargaSalida, dark: boolean, marcada: boolean, hoy = new Date(),
): { fondo: string; franja: string; alarma: boolean } {
  if (filaEnAlarma(c, hoy)) {
    return {
      fondo: marcada ? '' : (dark ? FILA_ALARMA.oscuro : FILA_ALARMA.claro),
      franja: dark ? FILA_ALARMA.franjaOscuro : FILA_ALARMA.franjaClaro,
      alarma: true,
    }
  }
  return { ...clasesFilaNivel(nivelDe(c), dark, marcada), alarma: false }
}

/**
 * ¿Está la línea sin reservar del todo? (`qtyReservada < qtySolicitada`).
 * Marca la CELDA «Reservada», no la fila — ver `DetalleCargaSalida`.
 */
export function reservaLineaIncompleta(l: Pick<CargaSalidaLinea, 'qtyReservada' | 'qtySolicitada'>): boolean {
  return l.qtyReservada < l.qtySolicitada
}

/**
 * Reserva de la línea contra ENTRADAS FUTURAS (mercancía que aún no ha llegado),
 * o `null` si no hay. Solo cuenta cuando es > 0: la segunda línea de la celda
 * «Reservada» no sale para el cero, el hueco ni un valor no numérico.
 * NO altera `reservaLineaIncompleta`: lo que no está en el almacén no está.
 */
export function reservaFuturaLinea(l: Pick<CargaSalidaLinea, 'qtyReservadaPedido'>): number | null {
  const f = l.qtyReservadaPedido
  return typeof f === 'number' && f > 0 ? f : null
}

/**
 * Los códigos de artículo de una carga, tal cual los trae la cabecera.
 *
 * La lista viene separada por `', '` (coma y espacio) —NO por `'|'` como
 * `pedidos`— y es sólo para el filtro. El recuento es `c.articulos`.
 */
export function articulosDe(c: CargaSalida): string[] {
  return (c.articulosBusqueda ?? '').split(',').map(a => a.trim()).filter(Boolean)
}

/** Diccionario código -> descripción a partir del catálogo del backend. Un
 *  artículo tiene siempre UNA descripción (medido), así que no se desambigua por
 *  empresa. Las descripciones `null` o en blanco no entran. */
export function descripcionesDe(
  catalogo: readonly ArticuloCatalogo[] | undefined,
): Map<string, string> {
  const m = new Map<string, string>()
  for (const a of catalogo ?? []) {
    const d = a.descripcion?.trim()
    if (d && !m.has(a.articulo)) m.set(a.articulo, d)
  }
  return m
}

/**
 * Opciones del filtro de artículo, en CASCADA: `cargas` ha de ser las que
 * quedan tras aplicar TODOS los demás filtros (el de artículo no, o al elegir
 * uno desaparecería el resto de opciones).
 *
 * La fuente de qué códigos existen son esas cargas (`articulosBusqueda`), NO el
 * catálogo: éste sólo es el diccionario de descripciones. Un código que no
 * esté en él se ofrece igualmente, sin descripción, en vez de desaparecer.
 */
export function opcionesArticuloCascada(
  cargas: readonly CargaSalida[],
  descripciones: ReadonlyMap<string, string>,
  fmt: FmtArticulo,
): OpcionMulti[] {
  return catalogoArticulos(
    cargas.flatMap(articulosDe), a => a, a => descripciones.get(a) ?? '', fmt,
  )
}

/**
 * ¿Cae la carga dentro del rango de fecha de salida? Los extremos son inclusivos
 * y `''` es «sin límite». ACOTA lo que entra; las bandas por proximidad siguen
 * agrupando lo que queda. Con rango activo, una carga sin fecha no cabe en él.
 */
export function enRangoFecha(c: CargaSalida, desde: string, hasta: string): boolean {
  if (!desde && !hasta) return true
  const f = c.fechaEnvioPrevista
  if (!f) return false
  if (desde && f < desde) return false
  if (hasta && f > hasta) return false
  return true
}

/**
 * Primer renglón de la celda de artículo de una línea: «código · color · estilo».
 * Color y estilo pueden llegar `null` o como cadena VACÍA (medido en artículos
 * ARF), y se tratan igual: la dimensión que no hay se omite.
 */
export function subtituloArticulo(
  codigo: string,
  color?: string | null,
  estilo?: string | null,
): string {
  return [codigo, color?.trim(), estilo?.trim()].filter(Boolean).join(' · ')
}

/** Días de retraso, o `null` si no va tarde ni tiene fecha. */
export function retrasoDe(c: CargaSalida, hoy = new Date()): number | null {
  const f = fechaDe(c.fechaEnvioPrevista)
  if (!f) return null
  const dias = diasRetraso(f, hoy)
  return dias > 0 ? dias : null
}

export type Incidencia =
  | 'atrasada'
  | 'sinFecha'
  | 'reservaIncompleta'
  | 'sinPeso'
  | 'pesoSospechoso'
  | 'sinPicking'

/**
 * Los avisos de una carga.
 *
 * `pesoSospechoso` es el bruto menor que el neto: pasa cuando el peso por caja
 * está mal informado en el maestro de artículos. Es un fallo del ORIGEN — se
 * marca y se enseña el número tal cual, no se corrige aquí.
 */
export function incidenciasDe(c: CargaSalida, hoy = new Date()): Incidencia[] {
  const avisos: Incidencia[] = []
  const f = fechaDe(c.fechaEnvioPrevista)
  if (!f) avisos.push('sinFecha')
  else if (diasRetraso(f, hoy) > 0) avisos.push('atrasada')
  if (!reservaDe(c).completa) avisos.push('reservaIncompleta')
  if (c.pesoNeto === null || c.pesoNeto === undefined) avisos.push('sinPeso')
  if (
    c.pesoBruto !== null && c.pesoBruto !== undefined &&
    c.pesoNeto !== null && c.pesoNeto !== undefined &&
    c.pesoBruto < c.pesoNeto
  ) avisos.push('pesoSospechoso')
  if (!c.tienePicking) avisos.push('sinPicking')
  return avisos
}

export interface TotalesCargas {
  cargas: number
  unidades: number
  reservadas: number
  picadas: number
  /** `null` si NINGUNA carga trae cajas: cero diría que no hay bultos. */
  cajas: number | null
  pesoNeto: number | null
  pesoBruto: number | null
  /** Cuántas cargas no traen el dato. Es lo que convierte un total en un total
   *  «a revisar» en vez de en una cifra que nadie pone en duda. */
  sinCajas: number
  sinPeso: number
  sospechosas: number
  pedidos: number
  clientes: number
}

/** Totales de la pantalla, contando los huecos en vez de sumarlos como ceros. */
export function totalesCargas(cargas: CargaSalida[]): TotalesCargas {
  let unidades = 0
  let reservadas = 0
  let picadas = 0
  let cajas = 0
  let neto = 0
  let bruto = 0
  let conCajas = 0
  let conNeto = 0
  let conBruto = 0
  let sinCajas = 0
  let sinPeso = 0
  let sospechosas = 0
  const pedidos = new Set<string>()
  const clientes = new Set<string>()

  for (const c of cargas) {
    unidades += c.qtySolicitada
    reservadas += c.qtyReservada
    picadas += c.qtyPicking
    if (c.cajas === null || c.cajas === undefined) sinCajas += 1
    else { cajas += c.cajas; conCajas += 1 }
    if (c.pesoNeto === null || c.pesoNeto === undefined) sinPeso += 1
    else { neto += c.pesoNeto; conNeto += 1 }
    if (c.pesoBruto !== null && c.pesoBruto !== undefined) { bruto += c.pesoBruto; conBruto += 1 }
    if (
      c.pesoBruto !== null && c.pesoBruto !== undefined &&
      c.pesoNeto !== null && c.pesoNeto !== undefined &&
      c.pesoBruto < c.pesoNeto
    ) sospechosas += 1
    for (const p of c.pedidos) pedidos.add(p)
    if (c.cliente) clientes.add(c.cliente)
  }

  return {
    cargas: cargas.length,
    unidades, reservadas, picadas,
    cajas: conCajas > 0 ? cajas : null,
    pesoNeto: conNeto > 0 ? neto : null,
    pesoBruto: conBruto > 0 ? bruto : null,
    sinCajas, sinPeso, sospechosas,
    pedidos: pedidos.size,
    clientes: clientes.size,
  }
}

/** ¿Los pedidos de la carga son de clientes distintos? La celda de cliente sólo
 *  puede enseñar uno, así que cuando esto es cierto hay que avisarlo. */
export function clientesDistintos(c: CargaSalida): boolean {
  // La cabecera trae UN cliente (`accountnum` de la carga). Que lleve más de un
  // pedido no implica más de un cliente, pero es la única señal que tenemos de
  // que la celda puede estar contando media verdad.
  return c.pedidos.length > 1
}

/**
 * ¿La próxima entrada de la línea es una fecha ya pasada que no llegó?
 *
 * Exige el flag Y que la fecha sea anterior a hoy: el tiempo verbal («venció» /
 * «llega») sale de la fecha, así que un flag a 1 con fecha futura no pinta rojo.
 */
export function entradaVencida(l: CargaSalidaLinea, hoy: Date): boolean {
  if (!l.proximaEntradaVencida) return false
  const f = fechaDe(l.proximaEntrada)
  return f != null && diasRetraso(f, hoy) > 0
}
